import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { PasswordInput } from '../../components/common/PasswordInput';
import { MagicLinkLoginForm } from '../../components/auth/MagicLinkLoginForm';
import { CaptchaWidget, type CaptchaWidgetHandle } from '../../components/common/CaptchaWidget';
import { usePlatformCaptchaConfig } from '../../hooks/useCaptchaConfig';
import { Button } from '../../ui';
import './auth.css';

type LoginMode = 'password' | 'magic-link';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [loginMode, setLoginMode] = useState<LoginMode>('password');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<CaptchaWidgetHandle | null>(null);

  const { login, requestMagicLink, error, clearError, pendingMfaChallenge, mfaSetupBlock } =
    useAuth();
  const { config: captchaConfig, shouldChallenge } = usePlatformCaptchaConfig();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: Location })?.from?.pathname || '/';

  // When the API responds with mfa_required, AuthContext stashes the
  // challenge state and we hand off to /login/mfa to collect the code.
  useEffect(() => {
    if (pendingMfaChallenge) {
      navigate('/login/mfa', { replace: true, state: { from: { pathname: from } } });
    }
  }, [pendingMfaChallenge, navigate, from]);

  const captchaRequired = shouldChallenge('admin_login');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    clearError();

    if (!email || !password) {
      setFormError('Please fill in all fields');
      return;
    }

    if (captchaRequired && !captchaToken) {
      setFormError('Please complete the CAPTCHA challenge.');
      return;
    }

    setIsSubmitting(true);
    try {
      await login({ email, password, captcha_token: captchaToken ?? undefined });
      navigate(from, { replace: true });
    } catch {
      // Error is handled by AuthContext. If the failure was a CAPTCHA reject,
      // ask the widget for a fresh token.
      captchaRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMagicLinkRequest = async (email: string, captchaToken?: string) => {
    clearError();
    await requestMagicLink(email, captchaToken);
  };

  const switchToMagicLink = () => {
    clearError();
    setFormError(null);
    setLoginMode('magic-link');
  };

  const switchToPassword = () => {
    clearError();
    setFormError(null);
    setLoginMode('password');
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Sign In</h1>
        <p className="auth-subtitle">Welcome back! Please sign in to continue.</p>

        {mfaSetupBlock && (
          <div className="auth-error" role="alert">
            {mfaSetupBlock.message}
            {mfaSetupBlock.deadline && (
              <>
                {' '}
                Deadline: <strong>{new Date(mfaSetupBlock.deadline).toLocaleString()}</strong>.
              </>
            )}{' '}
            Contact your organization owner to recover access.
          </div>
        )}

        {loginMode === 'magic-link' ? (
          <MagicLinkLoginForm
            onRequestLink={handleMagicLinkRequest}
            onBack={switchToPassword}
            error={error}
          />
        ) : (
          <>
            <form onSubmit={handleSubmit} className="auth-form">
              {(formError || error) && <div className="auth-error">{formError || error}</div>}

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  disabled={isSubmitting}
                />
              </div>

              <PasswordInput
                id="password"
                label="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={isSubmitting}
              />

              {captchaRequired && captchaConfig?.provider && captchaConfig.siteKey && (
                <CaptchaWidget
                  ref={captchaRef}
                  provider={captchaConfig.provider}
                  siteKey={captchaConfig.siteKey}
                  action="admin_login"
                  onVerify={setCaptchaToken}
                  onError={() => setCaptchaToken(null)}
                  onExpire={() => setCaptchaToken(null)}
                />
              )}

              <Button type="submit" block size="lg" loading={isSubmitting}>
                {isSubmitting ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="auth-divider">
              <span>or</span>
            </div>

            <div className="oauth-buttons">
              <button type="button" className="oauth-button magic-link" onClick={switchToMagicLink}>
                <Mail size={18} aria-hidden="true" />
                Sign in with email link
              </button>
              <a href="/api/auth/oauth/google" className="oauth-button google">
                Continue with Google
              </a>
              <a href="/api/auth/oauth/github" className="oauth-button github">
                Continue with GitHub
              </a>
            </div>
          </>
        )}

        <div className="auth-footer">
          <Link to="/forgot-password" className="auth-link">
            Forgot password?
          </Link>
          <span className="auth-separator">|</span>
          <span>
            Don't have an account?{' '}
            <Link to="/register" className="auth-link">
              Sign up
            </Link>
          </span>
        </div>
      </div>
    </div>
  );
}
